import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ExportButtons } from '@/components/export-buttons';
import { ResponsiveTable } from '@/components/responsive-table';
import { ListPagination, ScrollArea } from '@/components/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useVoterEmails } from '@/lib/api-hooks';
import { VOTER_EMAIL_PAGE_SIZE_OPTIONS, type VoterEmail } from '@/lib/emails';
import { useVoterEmailsStore } from '@/lib/emails-store';
import { type ExportFormat, downloadExport } from '@/lib/export';

export function EmailsPage() {
  const search = useVoterEmailsStore((state) => state.search);
  const page = useVoterEmailsStore((state) => state.page);
  const pageSize = useVoterEmailsStore((state) => state.pageSize);
  const setSearch = useVoterEmailsStore((state) => state.setSearch);
  const setPage = useVoterEmailsStore((state) => state.setPage);
  const setPageSize = useVoterEmailsStore((state) => state.setPageSize);

  const { data, isLoading } = useVoterEmails({ search, page, pageSize });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const activeFilters = search.trim() ? [`search: "${search.trim()}"`] : [];

  async function handleExport(format: ExportFormat) {
    try {
      await downloadExport('voter-emails', { search }, 'voter-emails', format);
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full sm:w-60">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search voter emails"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
        <ExportButtons onExport={handleExport} filters={activeFilters} />
      </div>

      <ScrollArea>
        <ResponsiveTable
          loading={isLoading}
          loadingLabel="Loading voter emails…"
          skeletonRows={pageSize}
          emptyLabel="No emails match your search."
          rows={items}
          rowKey={(row) => row.id}
          columns={[
            {
              key: 'email',
              header: 'Email',
              title: true,
              cell: (row: VoterEmail) => <span className="font-medium">{row.email}</span>,
            },
            {
              key: 'project',
              header: 'Project',
              cell: (row: VoterEmail) => (
                <Link
                  to={`/projects/${row.projectSlug}`}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  {row.project}
                </Link>
              ),
            },
            {
              key: 'artist',
              header: 'Artist voted for',
              cell: (row: VoterEmail) => (
                <Link
                  to={`/artists/${row.artistSlug}`}
                  className="hover:text-foreground hover:underline"
                >
                  {row.artist}
                </Link>
              ),
            },
            {
              key: 'date',
              header: 'Date',
              align: 'right',
              secondary: true,
              cell: (row: VoterEmail) => <span className="text-muted-foreground">{row.date}</span>,
            },
          ]}
        />
        {(total > 0 || isLoading) && (
          <ListPagination>
            <p className="text-muted-foreground text-sm">
              {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger size="sm" className="w-[4.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOTER_EMAIL_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                  <Button
                    key={number}
                    variant={number === page ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage(number)}
                    disabled={isLoading}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </ListPagination>
        )}
      </ScrollArea>
    </div>
  );
}
